import { useState, useRef, useCallback } from 'react';

interface StarRatingInputProps {
  rating: number;
  onRatingChange: (rating: number) => void;
  readonly?: boolean;
}

export function StarRatingInput({ rating, onRatingChange, readonly = false }: StarRatingInputProps) {
  const [hoverRating, setHoverRating] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateRating = useCallback((clientX: number) => {
    if (!containerRef.current) return 0;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const starWidth = rect.width / 5;
    const starIndex = Math.floor(x / starWidth);
    const starProgress = (x % starWidth) / starWidth;
    
    // 0.5 단위로 반올림
    const newRating = Math.min(5, Math.max(0, starIndex + (starProgress > 0.5 ? 1 : 0.5)));
    return newRating;
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (readonly) return;
    const newRating = calculateRating(e.clientX);
    setHoverRating(newRating);
  }, [calculateRating, readonly]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (readonly) return;
    const newRating = calculateRating(e.clientX);
    onRatingChange(newRating);
  }, [calculateRating, onRatingChange, readonly]);

  const handleMouseLeave = useCallback(() => {
    if (readonly) return;
    setHoverRating(0);
  }, [readonly]);

  const displayRating = hoverRating || rating;

  const renderStar = (index: number) => {
    const fillPercentage = Math.min(1, Math.max(0, displayRating - index));
    
    return (
      <div key={index} className="relative inline-block w-8 h-8">
        {/* Background star (empty) */}
        <svg
          className="absolute inset-0 w-8 h-8 text-gray-300"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
        
        {/* Filled star */}
        <div 
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${fillPercentage * 100}%` }}
        >
          <svg
            className="w-8 h-8 text-yellow-400"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center gap-2">
      {/* Stars */}
      <div
        ref={containerRef}
        className={`flex gap-1 ${readonly ? '' : 'cursor-pointer'}`}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {Array.from({ length: 5 }, (_, index) => renderStar(index))}
      </div>
      
      {/* Rating Display */}
      <div className="flex items-center gap-1 text-base font-semibold">
        <span>{displayRating.toFixed(1)}</span>
        <span className="text-gray-400">/</span>
        <span className="text-gray-400">5.0</span>
      </div>
    </div>
  );
}